import styles from './FreeTextQuestion.module.css'
import { FieldState, ValidationsForForm } from '@/interfaces/api/forms/forms_types'
import { SurveyFreeTextQuestion } from '@/interfaces/survey_types'
import { useSurveyStore } from '@/stores/useSurveyStore'
import { isRequired } from '@/utils/validators'
import { InputAdornment, TextField } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { validateForm } from '@/utils/helpers'
import TooltipField from '@/components/UI/Tooltips/TooltipField/TooltipField'
import { useTranslations } from 'next-intl'

interface FreeTextQuestionProps extends React.ComponentProps<'div'> {
  data: SurveyFreeTextQuestion
}

interface FreeTextQuestionFormState {
  [key: string]: FieldState<string>
}

export default function FreeTextQuestion ({ data }: FreeTextQuestionProps) {
  const t = useTranslations('Survey.FreeTextField')
  const { fields, setField } = useSurveyStore()

  const maxLengthRef = useRef<number>(data['max-len'] ?? 200)
  const maxLength = maxLengthRef.current

  const fieldNameRef = useRef<string>(data['set-field'] ?? `freeTextQuestion${crypto.randomUUID()}`)
  const fieldName: string = fieldNameRef.current

  // const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [formState, setFormState] = useState<FreeTextQuestionFormState>({
    [fieldName]: {
      value: fields[fieldName] && typeof fields[fieldName] === 'string' ? fields[fieldName] : '',
      dirty: false,
      required: true,
      validators: [
        { validator: isRequired, show: false }
      ]
    }
  })
  const [validations, setValidations] = useState<ValidationsForForm<FreeTextQuestionFormState>>({
    [fieldName]: []
  })

  /**
	 * Handles input changes for all fields
	 * Marks the field as `dirty` and updates its `value`.
	 * @param e Input change event from the TextField.
	 */
	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		const key = e.target.name as keyof FreeTextQuestionFormState
		setFormState(formStatePrev => {
      const currentField = formStatePrev[key]
      if (!currentField) return formStatePrev

			return {
				...formStatePrev,
				[key]: {
					...currentField,
					value: e.target.value.replace(/^\s+/, '').replace(/\s{2,}/g, ' '),
					dirty: true
				}
			}
		})

    if (data['set-field']) setField(data['set-field'], e.target.value.trim().replace(/\s{2,}/g, ' '))
	}

  /**
   * Re-validates the form whenever a field value changes
   * Updates both:
   * - `validations` (to show messages in the tooltips)
   * - `formIsValid` (to enable/disable the submit button)
   */
  useEffect(() => {
    const { validations: preValidations, isValid } = validateForm(formState, { [fieldName]: [] })
    setValidations(preValidations)
    // setFormIsValid(isValid)
  }, [formState])

  return(
    <div className={styles.freeTextField}>
      <p className={`labelText ${styles.freeTextField__label}`}>{data.title}</p>
      
      <TooltipField
        validations={validations[fieldName]}
        placement="bottom-start"
        arrow={false}
        disableHoverListener={true}
        disableTouchListener={true}
        hasHelperText={false}
      >
        <TextField
          type={'text'}
          name={fieldName}
          value={formState[fieldName].value}
          onChange={handleChange}
          multiline={true}
          minRows={2}
          slotProps={{
            htmlInput: {
              maxLength: maxLength,
            },
            input: {
              endAdornment:
              <InputAdornment
                position="end"
                sx={{
                  alignSelf: 'flex-end',
                  marginTop: 'auto'
                }}
              >
                <span className={`bodyTextSmall ${styles.charsInfo}`}>
                  {t.rich('textAreaCharsInfo', {
                    chars: formState[fieldName].value.length,
                    maxChars: maxLength,
                    span: (chunks) => <span className={formState[fieldName].value.length !== maxLength ? styles.enabledContent : ''}>{chunks}</span>
                  })}
                </span>
              </InputAdornment>
            }
          }}
        />
      </TooltipField>
    </div>
  )
}